<template>
  <main class="p-4">
    <input
      v-model="q"
      placeholder="Buscar producto..."
      class="w-full p-2 border rounded"
    />

    <div v-for="p in filteredProductos" :key="p.id">{{ p.nombre }}</div>

    <div class="p-4 max-w-xl mx-auto space-y-3">
      <h2 class="text-2xl font-bold">🛒 Crear Producto</h2>

      <input
        v-model="nombre"
        type="text"
        placeholder="Nombre del producto"
        class="w-full p-2 border rounded"
      />

      <select v-model="categoria" class="w-full p-2 border rounded">
        <option disabled value="">Selecciona categoría</option>
        <option v-for="cat in categorias" :key="cat.id" :value="cat.id">
          {{ cat.nombre }}
        </option>
      </select>

      <select v-model="tipo" class="w-full p-2 border rounded">
        <option value="producto">Producto</option>
        <option value="servicio">Servicio</option>
      </select>
      <!-- Mostrar en -->
      <select v-model="mostrarEn" class="w-full p-2 border rounded">
        <option disabled value="">Dónde mostrar</option>
        <option value="pos">Solo POS</option>
        <option value="ecommerce">Solo eCommerce</option>
        <option value="ambos">POS y eCommerce</option>
      </select>

      <!-- Visibilidad -->
      <label class="flex items-center mt-2 space-x-2">
        <input type="checkbox" v-model="visible" />
        <span>¿Mostrar producto al público?</span>
      </label>

      <div class="space-y-4">
        <div>
          <label class="block text-sm font-semibold mb-1">Costo unitario ($)</label>
          <input v-model.number="costo" type="number" class="w-full p-3 border rounded" />
        </div>

        <!-- Variantes -->
        <div class="space-y-3 border border-gray-300 rounded p-4 bg-gray-50">
          <label class="flex items-center space-x-2">
            <input type="checkbox" v-model="usaVariantes" />
            <span>¿Este producto tiene variantes?</span>
          </label>

            <div v-if="usaVariantes" class="space-y-3">
              <div>
                <label class="block text-sm font-semibold mb-1">🧩 Nombre del atributo principal</label>
                <input
                  v-model="atributoPrincipal"
                  placeholder="Ejemplo: color, tamaño, medida"
                class="p-2 border rounded w-full"
              />
            </div>

            <div v-if="atributoPrincipal.trim()" class="bg-white p-3 border rounded shadow-sm space-y-2">
              <h4 class="font-semibold text-sm text-blue-600">
                Variantes de {{ atributoPrincipal }}
              </h4>
              <input
                v-model="nuevaVariante.valor"
                :placeholder="`Valor de ${atributoPrincipal} (ej: azul, rojo, M)`"
                class="border p-2 rounded w-full"
              />
              <input
                v-model="nuevaVariante.descripcion"
                placeholder="Descripción (opcional)"
                class="border p-2 rounded w-full"
              />
              <input
                v-model.number="nuevaVariante.precio"
                type="number"
                placeholder="Precio ($) - opcional"
                class="border p-2 rounded w-full"
              />
              <div class="flex gap-2">
                <button
                  @click="agregarVariante"
                  class="flex-1 bg-blue-600 text-white px-4 py-2 rounded font-semibold"
                >
                  Agregar variante
                </button>
                <button
                  @click="guardarAtributoConVariantes"
                  class="flex-1 bg-green-600 text-white px-4 py-2 rounded font-semibold"
                >
                  Guardar atributo y variantes
                </button>
              </div>
            </div>

            <div v-if="variantesTemporales.length" class="space-y-2">
              <h5 class="font-semibold text-sm text-gray-800">
                Variantes en edición de <strong class="capitalize">{{ atributoPrincipal }}</strong>
              </h5>
              <ul class="space-y-1 text-sm text-gray-700">
                <li
                  v-for="(v, index) in variantesTemporales"
                  :key="index"
                  class="flex justify-between items-center border-b pb-1"
                >
                  <span>
                    {{ atributoPrincipal }}: {{ v.valor || '—' }}
                    <span v-if="v.descripcion"> - {{ v.descripcion }}</span>
                    <span v-if="v.precio !== null && v.precio !== undefined">
                      - ${{ v.precio.toFixed(2) }}
                    </span>
                  </span>
                  <button
                    @click="eliminarVarianteTemp(index)"
                    class="text-red-500 text-xs hover:underline"
                  >
                    Eliminar
                  </button>
                </li>
              </ul>
            </div>

            <div v-if="atributosConVariantes.length" class="mt-3 space-y-2">
              <h5 class="font-semibold text-sm text-gray-800">Variantes guardadas</h5>
              <div
                v-for="(attr, idx) in atributosConVariantes"
                :key="idx"
                class="border rounded p-2 text-sm text-gray-700"
              >
                <p class="font-semibold capitalize">{{ attr.atributo }}</p>
                <ul class="pl-4 list-disc space-y-1">
                  <li v-for="(v, j) in attr.variantes" :key="j">
                    {{ v.valor || '—' }}<span v-if="v.precio !== null && v.precio !== undefined"> - ${{ Number(v.precio).toFixed(2) }}</span>
                    <span v-if="v.descripcion"> - {{ v.descripcion }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div>
          <label class="block text-sm font-semibold mb-1">Margen de ganancia (%)</label>
          <input
            v-model.number="margen"
            type="number"
            class="w-full p-3 border rounded"
            @input="calcularPrecio"
          />
        </div>

        <div>
          <label class="block text-sm font-semibold mb-1">Precio de venta sugerido ($)</label>
          <input
            :value="(precioCalculado ?? 0).toFixed(2)"
            disabled
            class="w-full p-2 border rounded bg-gray-100 text-gray-700"
          />
        </div>

        <div v-if="usaVariantes" class="text-sm text-yellow-700 bg-yellow-100 border border-yellow-300 p-2 rounded">
          ⚠️ Este producto tiene variantes. El precio mostrado aquí será reemplazado por el precio de la variante seleccionada en el eCommerce.
        </div>


        <div v-if="tipo === 'servicio'">
          <label class="block text-sm font-semibold mb-1"
            >Duración del servicio (minutos)</label
          >
          <input
            v-model.number="duracion"
            type="number"
            class="w-full p-2 border rounded"
          />
        </div>

        <div>
          <label class="block text-sm font-semibold mb-1"
            >Cantidad en inventario</label
          >
          <input
            v-model.number="inventario"
            type="number"
            class="w-full p-3 border rounded"
          />
        </div>

        <div>
          <label class="block text-sm font-semibold mb-1"
            >Stock mínimo para alerta</label
          >
          <input
            v-model.number="stockMinimo"
            type="number"
            class="w-full p-3 border rounded"
          />
        </div>
      </div>

      <select v-model="taxId" class="w-full p-2 border rounded">
        <option disabled value="">Selecciona impuesto</option>
        <option v-for="tax in taxes" :key="tax.id" :value="tax.id">
          {{ tax.nombre }} - {{ tax.porcentaje }}%
        </option>
      </select>

      <div class="space-y-2">
        <label class="block text-sm font-semibold mb-1">Foto principal</label>
        <input type="file" accept="image/*" @change="onFileChange" class="w-full" />
      </div>

      <div class="space-y-2">
        <label class="block text-sm font-semibold mb-1">Galería eCommerce (mín. 4 fotos)</label>
        <input type="file" multiple accept="image/*" @change="onGaleriaChange" class="w-full" />
        <p class="text-xs text-gray-500">Se usa en la tienda tipo Shopify. Toma la primera como portada si no subes una principal.</p>
        <p v-if="galeriaArchivos.length" class="text-xs text-green-700">
          {{ galeriaArchivos.length }} imagen{{ galeriaArchivos.length === 1 ? '' : 'es' }} seleccionada{{ galeriaArchivos.length === 1 ? '' : 's' }}.
        </p>
      </div>

      <button
        @click="crearProducto"
        class="w-full bg-green-600 text-white py-2 rounded font-bold"
      >
        Guardar producto
      </button>
    </div>
  </main>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { db, storage } from "@/firebase";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { ref as sRef, uploadBytes, getDownloadURL } from "firebase/storage";

// Datos del producto
const productoCreadoId = ref(null);
const nombre = ref("");
const categoria = ref("");
const tipo = ref("producto");
const costo = ref(0);
const margen = ref(20);
const precio = ref(0);
const inventario = ref(0);
const stockMinimo = ref(0);
const taxId = ref("");
const mostrarEn = ref("ambos");
const visible = ref(true);
const duracion = ref(60);
const file = ref(null);
const galeriaArchivos = ref([]);

// Variantes
const usaVariantes = ref(false);
const atributoPrincipal = ref("");
const variantesTemporales = ref([]); // Aquí se guardan las variantes antes de consolidarlas
const atributosConVariantes = ref([]); // Estructura final con atributo y sus variantes

const nuevaVariante = ref({
  valor: "",
  descripcion: "",
  precio: null
});

// Agregar una variante temporal al atributo actual
const agregarVariante = () => {
  const attr = atributoPrincipal.value.trim();
  const valor = nuevaVariante.value.valor?.trim();

  if (!attr) {
    alert("Primero debes definir el nombre del atributo principal.");
    return;
  }

  if (!valor) {
    alert(`Agrega un valor para ${attr} (ej: azul, rojo, M).`);
    return;
  }

  variantesTemporales.value.push({
    valor,
    descripcion: nuevaVariante.value.descripcion?.trim() || "",
    precio: nuevaVariante.value.precio !== null
      ? Number(nuevaVariante.value.precio)
      : null
  });

  // Reset
  nuevaVariante.value = {
    valor: "",
    descripcion: "",
    precio: null
  };
};

// Eliminar una variante temporal
const eliminarVarianteTemp = (index) => {
  variantesTemporales.value.splice(index, 1);
};

// Guardar atributo con todas sus variantes
const guardarAtributoConVariantes = () => {
  const attr = atributoPrincipal.value.trim();

  if (!attr) {
    alert("El nombre del atributo es obligatorio.");
    return;
  }

  if (variantesTemporales.value.length === 0) {
    alert("Debes agregar al menos una variante antes de guardar.");
    return;
  }

  atributosConVariantes.value.push({
    atributo: attr,
    variantes: [...variantesTemporales.value]
  });

  // Reset
  atributoPrincipal.value = "";
  variantesTemporales.value = [];
  nuevaVariante.value = {
    valor: "",
    descripcion: "",
    precio: null
  };
};


const calcularPrecio = () => {
  precio.value = costo.value + (costo.value * margen.value) / 100;
};

const onFileChange = (e) => (file.value = e.target.files[0]);
const onGaleriaChange = (e) => {
  const incoming = Array.from(e.target.files || []);
  if (!incoming.length) return;
  // Permite seleccionar en varias tandas: acumular y evitar duplicados por name+size
  const existingKey = new Set(galeriaArchivos.value.map(f => `${f.name}-${f.size}`));
  for (const f of incoming) {
    const key = `${f.name}-${f.size}`;
    if (!existingKey.has(key)) galeriaArchivos.value.push(f);
  }
};

// Cargar categorías e impuestos
const categorias = ref([]);
const taxes = ref([]);

onMounted(async () => {
  const catSnap = await getDocs(collection(db, "categorias"));
  categorias.value = catSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

  const taxSnap = await getDocs(collection(db, "impuestos"));
  taxes.value = taxSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
});

const precioCalculado = computed(() => {
  const c = Number(costo.value);
  const m = Number(margen.value);
  if (isNaN(c) || isNaN(m)) return 0;
  return c + (c * m) / 100;
});

const crearProducto = async () => {
  const nombreTrim = (nombre.value || "").trim();
  const costoNumber = Number(costo.value);
  const margenNumber = Number(margen.value);

  if (!nombreTrim || !categoria.value) {
    alert("Faltan campos obligatorios");
    return;
  }

  if (!Number.isFinite(costoNumber) || !Number.isFinite(margenNumber)) {
    alert("Costo y margen deben ser números válidos (pueden ser 0).");
    return;
  }

  const mostrarEnEcommerce = ["ambos", "ecommerce"].includes(mostrarEn.value);
  // Reglas más flexibles: al menos 1 foto total para eCommerce (portada o galería)
  const totalFotos = (file.value ? 1 : 0) + galeriaArchivos.value.length;
  if (mostrarEnEcommerce && totalFotos < 1) {
    alert("Para eCommerce sube al menos 1 foto (portada o en la galería).");
    return;
  }

  // Consolidar variantes: combina las temporales actuales + las guardadas
  const variantesParaGuardar = [];
  if (variantesTemporales.value.length) {
    const attr = atributoPrincipal.value.trim() || "Atributo";
    variantesParaGuardar.push(
      ...variantesTemporales.value.map(v => ({ ...v, atributo: attr }))
    );
  }
  for (const grupo of atributosConVariantes.value) {
    if (Array.isArray(grupo?.variantes)) {
      variantesParaGuardar.push(
        ...grupo.variantes.map(v => ({ ...v, atributo: grupo.atributo || "Atributo" }))
      );
    }
  }
  const atributosList = [...new Set(variantesParaGuardar.map(v => (v.atributo || "").trim()).filter(Boolean))];
  const atributoPrincipalDoc =
    atributoPrincipal.value.trim() ||
    atributosList[0] ||
    atributosConVariantes.value[0]?.atributo?.trim() ||
    "";

  const finalPrecio = precioCalculado.value;
  let imagenUrl = "";
  let imagePath = "";
  const galeriaUrls = [];

  const mostrarWebBool = ["ambos", "ecommerce"].includes(mostrarEn.value);
  const mostrarPOSBool = ["ambos", "pos"].includes(mostrarEn.value);

  const requiereAprobacion =
    tipo.value === "servicio" &&
    (nombre.value || "").toLowerCase().includes("laser");

  // Crear producto base
  const docRef = await addDoc(collection(db, "productos"), {
    nombre: nombreTrim,
    categoria: categoria.value,
    tipo: tipo.value,
    costo: costoNumber,
    margen: margenNumber,
    precio: Number(finalPrecio),
    stock: Number(inventario.value),
    stockMinimo: Number(stockMinimo.value),
    taxId: taxId.value,
    impuesto: 0,
    imagenUrl: "",
    imagePath: "",
    mostrarEn: mostrarEn.value,
    mostrarWeb: mostrarWebBool,
    mostrarPOS: mostrarPOSBool,
    visible: Boolean(visible.value),
    activo: true,
    creado: new Date(),
    duracion: tipo.value === "servicio" ? Number(duracion.value) : 0,
    requiereAprobacion,
    usaVariantes: usaVariantes.value,
    atributoPrincipal: usaVariantes.value ? atributoPrincipalDoc : "",
    atributos: usaVariantes.value ? atributosList : []
  });
  productoCreadoId.value = docRef.id;
  
  // Subir imagen si hay
  if (file.value) {
    imagePath = `productos/${docRef.id}/main.jpg`;
    const imgRef = sRef(storage, imagePath);
    await uploadBytes(imgRef, file.value);
    imagenUrl = await getDownloadURL(imgRef);
  }

  // Subir galería eCommerce
  if (galeriaArchivos.value.length) {
    for (const [i, img] of galeriaArchivos.value.entries()) {
      const path = `productos/${docRef.id}/galeria/${i}-${img.name}`;
      const gRef = sRef(storage, path);
      await uploadBytes(gRef, img);
      galeriaUrls.push(await getDownloadURL(gRef));
    }
  }

  // Aplicar impuesto si hay
  let taxPct = 0;
  if (taxId.value) {
    const taxDoc = await getDoc(doc(db, "impuestos", taxId.value));
    if (taxDoc.exists()) {
      taxPct = Number(taxDoc.data()?.porcentaje || 0);
    }
  }

  // Actualizar doc con imagen e impuesto
  await updateDoc(docRef, {
    imagenUrl: imagenUrl || galeriaUrls[0] || "",
    imagePath,
    galeriaEcom: galeriaUrls,
    impuesto: taxPct
  });

  // Guardar variantes
  if (usaVariantes.value && variantesParaGuardar.length > 0) {
    const variantesRef = collection(db, `productos/${docRef.id}/variantes`);
    for (const variante of variantesParaGuardar) {
      await addDoc(variantesRef, {
        valor: variante.valor,
        atributo: variante.atributo || atributoPrincipalDoc || atributosList[0] || "",
        descripcion: variante.descripcion || "",
        precio: variante.precio ?? null,
        creado: new Date()
      });
    }
  }

  // Reset
  nombre.value = "";
  categoria.value = "";
  tipo.value = "producto";
  costo.value = 0;
  margen.value = 20;
  precio.value = 0;
  inventario.value = 0;
  stockMinimo.value = 0;
  taxId.value = "";
  mostrarEn.value = "ambos";
  visible.value = true;
  duracion.value = 60;
  file.value = null;
  galeriaArchivos.value = [];
  usaVariantes.value = false;
  atributoPrincipal.value = "";
  variantesTemporales.value = [];
  atributosConVariantes.value = [];

  alert("✅ Producto creado correctamente");
};
</script>
